// TODO - remove lint disable when editing this file
/* eslint-disable */
import { Map, List } from 'immutable'
import _ from 'lodash'
import { EditorState } from 'draft-js'
import {
  applyStyle,
  removeStyles,
  updateBlock,
  isAtomicBlock,
} from '../../pluginCommon/pluginHelpers'

const STORY_TYPE = 'story'

export const commentDefaultStyle = {
  backgroundColor: 'rgba(90, 212, 255, 0.2)',
}

export const getCommentStyleType = (blockKey, index) =>
  `COMMENT-${blockKey}-${index}`

export const getComments = (elementMetaData) => {
  return elementMetaData
    .get('additionalProperties', Map())
    .get('comments', List())
}

export const getCommentByIndex = (elementMetaData, index) => {
  return getComments(elementMetaData).get(index, Map())
}

export const getReplies = (comment) => {
  return (comment || Map()).get('replies', List())
}

export const getReplyByIndex = (comment, replyIndex) => {
  return getReplies(comment).get(replyIndex, Map())
}

export const setComment = (elementMetaData, comment, index) => {
  return elementMetaData.updateIn(
    ['additionalProperties', 'comments'],
    List(),
    (comments) => comments.set(index, comment)
  )
}

export const setReply = (elementMetaData, commentIndex, reply, index) => {
  const newComment = getCommentByIndex(elementMetaData, commentIndex).updateIn(
    ['replies'],
    List(),
    (replies) => replies.set(index, reply)
  )
  return setComment(elementMetaData, newComment, commentIndex)
}

export const findStyle = (block, styleType) => {
  const characterList = block.getCharacterList()
  let start = -1
  let end = -1
  characterList.forEach((characterMetadata, charIndex) => {
    if (characterMetadata.hasStyle(styleType)) {
      if (!~start) {
        start = charIndex
      }
      end = charIndex + 1
    }
  })

  return ~start ? { start, end } : null
}

export const removeStyle = (block, styleType, start = 0, end) => {
  return removeStyles(
    block,
    (characterMetadata) => {
      return characterMetadata.hasStyle(styleType) ? [styleType] : []
    },
    start,
    end
  )
}

export const isLegacyComment = (comment) => {
  return comment.get('start') === void 0 || comment.get('end') === void 0
}

export const updateEditorState = (
  setEditorState,
  editorState,
  block,
  eventType
) => {
  const updatedBlock = updateBlock(editorState.getCurrentContent(), block)
  const updatedState = eventType
    ? EditorState.push(
        editorState,
        updatedBlock,
        // $FlowIgnoreMe We have customized event type so we can properly handle undo behavior after re-applying comment style
        eventType
      )
    : EditorState.set(editorState, { currentContent: updatedBlock })
  setEditorState(updatedState)
}

export const copyComments = (
  commentCache,
  fromComments,
  toBlock,
  addStyleType
) => {
  const toBlockKey = toBlock.getKey()
  let elementMetaData = toBlock.getData()
  let size = getComments(elementMetaData).size
  let comment
  let newStyleType
  _.forEach(fromComments, ({ blockKey, index, start, end }, styleType) => {
    comment = commentCache[blockKey].get(index, Map())
    if (comment.get('text')) {
      newStyleType = getCommentStyleType(toBlockKey, size)
      elementMetaData = setComment(elementMetaData, comment, size)
      toBlock = applyStyle(toBlock, newStyleType, start, end)
      if (_.isFunction(addStyleType)) {
        addStyleType(newStyleType)
      }
      size++
    }

    toBlock = removeStyle(
      toBlock,
      getCommentStyleType(blockKey, index),
      start,
      end
    )
  })

  return toBlock.set('data', elementMetaData)
}

export const isSupportedType = (block) => {
  // Comments data in the additional_properties for story type is different in structure and usage
  if (
    block &&
    block.getData().get('ellipsisType') !== `ellipsis.${STORY_TYPE}`
  ) {
    return true
  }
  return false
}

export const getCommentAfterToBlocksMiddleware = (
  commentStyle = commentDefaultStyle
) => (element, insertedBlocks, context) => {
  // Do nothing if type is story
  if (element.type === STORY_TYPE) {
    return insertedBlocks
  }

  const customStyleMap = {}
  insertedBlocks = insertedBlocks.map((block) => {
    if (isAtomicBlock(block)) {
      return block
    }

    const comments = getComments(block.getData()).toJS()
    const blockKey = block.getKey()
    let styleType

    comments.forEach((comment, index) => {
      if (comment.start !== void 0 && comment.end !== void 0) {
        styleType = getCommentStyleType(blockKey, index)
        block = applyStyle(block, styleType, comment.start, comment.end)
        customStyleMap[styleType] = commentStyle
      }
    })

    return block
  })

  context.replaceBlocks(insertedBlocks)
  context.mergeCustomStyleMap(customStyleMap)

  return insertedBlocks
}

export const commentBeforeToRawMiddleware = (contentState, block) => {
  // Do nothing if it's not supported type
  if (!isSupportedType(block)) {
    return {
      contentState,
      block,
    }
  }

  const blockKey = block.getKey()
  const isAtomic = isAtomicBlock(block)
  const comments = getComments(block.getData())
  let existingComments = List()
  let newBlock = block
  let styleType
  let range

  // Sync back the selection range to the comment data
  comments.forEach((comment, index) => {
    if (comment.get('text')) {
      if (comment.get('replies')) {
        const replies = comment
          .get('replies')
          .filter((reply) => !!reply.get('text'))
        comment = replies.size
          ? comment.set('replies', replies)
          : comment.delete('replies')
      }

      if (isLegacyComment(comment) || isAtomic) {
        // Legacy comments without selection range or atomic block
        // Do force conversion here
        existingComments = existingComments.push(
          comment.merge({ start: 0, end: 0 })
        )
      } else {
        styleType = getCommentStyleType(blockKey, index)
        range = findStyle(block, styleType)
        if (range) {
          newBlock = removeStyle(newBlock, styleType, range.start, range.end)
          comment = comment.merge(range)
          existingComments = existingComments.push(comment)
        }
      }
    }
  })

  newBlock = newBlock.set(
    'data',
    newBlock
      .getData()
      .setIn(['additionalProperties', 'comments'], existingComments)
  )
  return {
    contentState: updateBlock(contentState, newBlock),
    block: newBlock,
  }
}
